export const MULTI_LINE_VALUATION_FEATURE = 'multi_line_valuation';

export const LINE_FEATURES = {
  wealth: 'multi_line_valuation_wealth_management',
  life: 'multi_line_valuation_life_insurance',
  pc: 'multi_line_valuation_p_and_c',
  group: 'multi_line_valuation_group',
};

export const LINE_LABELS = {
  wealth: 'Wealth',
  life: 'Life',
  pc: 'P&C',
  group: 'Group',
};

function num(v) {
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function featureList(company) {
  const ft = company?.feature_types;
  if (Array.isArray(ft)) return ft.map(String);
  const f = company?.features;
  if (Array.isArray(f)) return f.map(String);
  return [];
}

export function isMultiLineValuationEnabled(company) {
  const features = featureList(company);
  return (
    features.includes(MULTI_LINE_VALUATION_FEATURE) ||
    Object.values(LINE_FEATURES).some((f) => features.includes(f))
  );
}

export function enabledLines(company) {
  const features = featureList(company);
  // If the overall flag is on, enable whatever line flags are enabled; otherwise treat multi-line as off.
  const multiLine = isMultiLineValuationEnabled(company);
  if (!multiLine) return ['wealth'];

  const lines = Object.entries(LINE_FEATURES)
    .filter(([, featureName]) => features.includes(featureName))
    .map(([line]) => line);

  return lines.length > 0 ? lines : ['wealth'];
}

export function computeMultiLineRevenue(user, company) {
  const lines = enabledLines(company);

  const wealthGrossFromParts =
    num(user?.fee_based_revenue) +
    num(user?.trailer_revenue) +
    num(user?.direct_revenue) +
    num(user?.transactional_revenue);
  const wealthGross = wealthGrossFromParts > 0 ? wealthGrossFromParts : (num(user?.gross_production) || num(user?.annual_revenue));
  const wealthRecurring =
    (num(user?.fee_based_revenue) + num(user?.trailer_revenue) + num(user?.direct_revenue)) ||
    num(user?.recurring_revenue);
  const wealthNonRecurring = Math.max(0, wealthGross - wealthRecurring);

  const lifeGross =
    num(user?.life_year_1_gross) ||
    (num(user?.li_first_year_commissions) +
      num(user?.li_renewal_commissions) +
      num(user?.li_override_commissions) +
      num(user?.li_other_revenues));
  const lifeRecurring = num(user?.li_renewal_commissions);
  const lifeNonRecurring = Math.max(0, lifeGross - lifeRecurring);

  const groupGross =
    num(user?.group_year_1_gross) ||
    (num(user?.group_first_year_commissions) +
      num(user?.group_renewal_commissions) +
      num(user?.group_override_commissions) +
      num(user?.group_other_revenues));
  const groupRecurring = num(user?.group_renewal_commissions);
  const groupNonRecurring = Math.max(0, groupGross - groupRecurring);

  const pcGrossFromPremiums =
    (num(user?.pc_personal_lines_premium) * (num(user?.pc_personal_lines_commission_rate) / 100)) +
    (num(user?.pc_commercial_lines_premium) * (num(user?.pc_commercial_lines_commission_rate) / 100));
  const pcGross = num(user?.pc_year_1_commission) || pcGrossFromPremiums;
  // P&C is effectively recurring revenue in this simplified model (annual renewing commissions)
  const pcRecurring = pcGross;
  const pcNonRecurring = 0;

  const byLine = {
    wealth: { gross: wealthGross, recurring: wealthRecurring, nonRecurring: wealthNonRecurring },
    life: { gross: lifeGross, recurring: lifeRecurring, nonRecurring: lifeNonRecurring },
    pc: { gross: pcGross, recurring: pcRecurring, nonRecurring: pcNonRecurring },
    group: { gross: groupGross, recurring: groupRecurring, nonRecurring: groupNonRecurring },
  };

  const activeByLine = Object.fromEntries(lines.map((l) => [l, byLine[l]]));
  const totalGross = lines.reduce((sum, l) => sum + num(activeByLine[l]?.gross), 0);
  const totalRecurring = lines.reduce((sum, l) => sum + num(activeByLine[l]?.recurring), 0);
  const totalNonRecurring = Math.max(0, totalGross - totalRecurring);

  return {
    lines,
    byLine: activeByLine,
    totals: {
      gross: totalGross,
      recurring: totalRecurring,
      nonRecurring: totalNonRecurring,
    },
  };
}

export function computeRecurringNonRecurringPercentages({ user, company }) {
  const multiLine = isMultiLineValuationEnabled(company);
  if (multiLine) {
    const { totals } = computeMultiLineRevenue(user, company);
    if (totals.gross > 0) {
      const recurring = totals.recurring / totals.gross;
      return { recurring, nonRecurring: Math.max(0, 1 - recurring) };
    }
  }

  const recurring = num(user?.percent_revenue_recurring);
  let nonRecurring = num(user?.percent_non_recurring_revenue);

  // If backend values don't sum to ~1, prefer derived non-recurring.
  if (recurring > 0 && (nonRecurring <= 0 || Math.abs((recurring + nonRecurring) - 1) > 0.02)) {
    nonRecurring = Math.max(0, 1 - recurring);
  }

  return { recurring, nonRecurring };
}

// `transactional_revenue` is a store attribute that defaults to 1.0 (see
// app/models/user.rb), so a bare $1 means the advisor never supplied a
// breakdown — not that they earn a dollar of commission. Treated as unset
// wherever it is read, so it never becomes a slice or props up a total.
const TRANSACTIONAL_PLACEHOLDER = 1;

// Only wealth_rj derives its fee/commission split from these revenue
// components (User#calculate_percent_fee_based). Every other business type
// reads the self-reported percent_fee_based/percent_commission columns, which
// are authoritative for those tenants and must not be second-guessed here.
const COMPONENT_DERIVED_BUSINESS_TYPE = 'wealth_rj';

// Ordered: determines slice order. Colour is pinned per stream rather than by
// position so a stream keeps its colour across advisors, and the palette starts
// at accent/neutral to stay distinguishable from the Revenue Mix donut beside
// it (which uses primary/secondary).
const COMPENSATION_COMPONENTS = [
  { id: 'fee_based', field: 'fee_based_revenue', labelKey: 'stream_fee_based', colorToken: 'accent' },
  { id: 'trail', field: 'trailer_revenue', labelKey: 'stream_trail', colorToken: 'neutral' },
  { id: 'commission', field: 'transactional_revenue', labelKey: 'stream_commission', colorToken: 'primary' },
  { id: 'direct', field: 'direct_revenue', labelKey: 'stream_direct', colorToken: 'secondary' },
];

export const COMPENSATION_MODES = {
  // Per-stream breakdown from real revenue components.
  STREAMS: 'streams',
  // The stored fee-vs-commission split (self-reported, non-wealth_rj tenants).
  PERCENTAGES: 'percentages',
  // Nothing trustworthy to chart.
  UNAVAILABLE: 'unavailable',
};

function componentValue(user, field) {
  const value = num(user?.[field]);
  if (field === 'transactional_revenue' && value === TRANSACTIONAL_PLACEHOLDER) return 0;
  return value;
}

// Breaks compensation down by revenue stream rather than forcing an either/or
// fee-vs-commission split — a trail is a recurring commission, so it belongs to
// neither bucket cleanly. Zero streams are dropped so the chart only shows what
// the advisor actually earns.
//
// Returns a `mode` telling the caller which chart is defensible. Crucially,
// UNAVAILABLE rather than PERCENTAGES when a wealth_rj advisor has no
// components: for those tenants the stored percentages are *derived from* these
// same components, so falling back to them would restate missing data as a
// confident "Commission Based 100%".
export function computeCompensationMix({ user, company }) {
  const components = COMPENSATION_COMPONENTS
    .map((component) => ({ ...component, value: componentValue(user, component.field) }))
    .filter((component) => component.value > 0);

  const total = components.reduce((sum, component) => sum + component.value, 0);

  if (company?.business_type !== COMPONENT_DERIVED_BUSINESS_TYPE) {
    return { mode: COMPENSATION_MODES.PERCENTAGES, components: [], total: 0 };
  }

  if (total <= 0) {
    return { mode: COMPENSATION_MODES.UNAVAILABLE, components: [], total: 0 };
  }

  return { mode: COMPENSATION_MODES.STREAMS, components, total };
}

export function computeRevenueMixByLine({ user, company }) {
  const multiLine = isMultiLineValuationEnabled(company);
  if (!multiLine) return [];

  const { lines, byLine, totals } = computeMultiLineRevenue(user, company);
  if (!totals.gross || totals.gross <= 0) return [];

  return lines
    .map((line) => ({
      id: line,
      label: LINE_LABELS[line] || line,
      value: num(byLine[line]?.gross),
    }))
    .filter((x) => x.value > 0);
}


